import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { usePermissions } from "@/hooks/usePermissions";
import {
  getAnalytics,
  type AnalyticsParams,
  type AnalyticsPeriod,
  type AnalyticsResponse,
  type LeaderboardBoard,
} from "@/api/analytics";
import { listMembers } from "@/api/projects";
import { getEffectivePipelineStatuses } from "@/lib/projectConfig";
import type { ProjectMember } from "@/api/types";
import { AnalyticsHeader } from "@/components/analytics/AnalyticsHeader";
import { KpiCards } from "@/components/analytics/KpiCards";
import { FunnelChart } from "@/components/analytics/FunnelChart";
import { ConversionMetrics } from "@/components/analytics/ConversionMetrics";
import { TrendsChart } from "@/components/analytics/TrendsChart";
import { OutreachStats } from "@/components/analytics/OutreachStats";
import { TeamPerformance } from "@/components/analytics/TeamPerformance";
import { Leaderboard } from "@/components/analytics/Leaderboard";
import { BreakdownCharts } from "@/components/analytics/BreakdownCharts";
import { RecentWins } from "@/components/analytics/RecentWins";
import { DealAttribution } from "@/components/analytics/DealAttribution";
import { ErrorBoundary } from "@/components/ErrorBoundary";

type LoadStatus = "loading" | "ok" | "error";

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { project } = useCurrentProject();
  const perms = usePermissions();
  const slug = project?.slug ?? null;

  // Route guard — gated strictly on analytics.view, matching the
  // backend. Users redirected here without the perm land on the
  // project dashboard.
  useEffect(() => {
    if (slug && !perms.canViewAnalytics) {
      toast.info("You don't have access to this page.");
      navigate(`/projects/${slug}/dashboard`, { replace: true });
    }
  }, [slug, perms.canViewAnalytics, navigate]);

  // Filters — period defaults to 30d (most representative window for
  // a sales pipeline) and owner defaults to the whole team.
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");
  const [customRange, setCustomRange] = useState<
    { start: string; end: string } | null
  >(null);
  const [ownerId, setOwnerId] = useState<string>("all");
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Members fetched once per project — used by the owner filter
  // dropdown and the team performance section's avatar fallbacks.
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    listMembers(slug)
      .then((m) => !cancelled && setMembers(m))
      .catch(() => !cancelled && setMembers([]));
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const params = useMemo<AnalyticsParams>(() => {
    const p: AnalyticsParams = { period };
    if (period === "custom" && customRange) {
      p.start = customRange.start;
      p.end = customRange.end;
    }
    if (ownerId !== "all") p.owner_id = ownerId;
    return p;
  }, [period, customRange, ownerId]);

  const load = useCallback(async () => {
    if (!slug) return;
    setStatus("loading");
    setErrorMessage(null);
    try {
      const res = await getAnalytics(slug, params);
      setData(res);
      setStatus("ok");
    } catch (err) {
      setErrorMessage(extractMessage(err));
      setStatus("error");
    }
  }, [slug, params]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!perms.canViewDashboard || !project) return null;

  const loading = status === "loading";
  // Pipeline config drives the funnel's filter (drop is_lost /
  // is_disqualified) and per-stage label / colour overrides so the
  // chart matches what the user sees on the kanban.
  const pipelineStatuses = getEffectivePipelineStatuses(project);

  // Pull every section's data into a locally-scoped variable with a
  // safe default. Each line reads the canonical backend field name
  // first (e.g. `by_owner`, `leaderboard`, flat breakdowns,
  // `deal_attribution`) and falls back to the legacy shape so the
  // page works against either generation of the API. Destructuring
  // here also keeps `data?.foo.bar`-style undefined-deref crashes
  // out of the JSX.
  const kpis = data?.kpis ?? [];
  const funnel = data?.funnel ?? [];
  const conversion = data?.conversion ?? [];
  const trendsSeries = data?.trends?.series ?? [];
  const trendsBuckets = data?.trends?.buckets ?? [];
  const outreach = data?.outreach ?? [];

  // by_owner is the canonical name; `team` was the legacy alias.
  const team = data?.by_owner ?? data?.team ?? [];

  // The backend ships leaderboard as an object with four metric
  // arrays; the Leaderboard component expects an array of board
  // descriptors. Fan out here so the component contract stays
  // unchanged. If a legacy backend still ships `leaderboards: []`,
  // we use it directly.
  const leaderboards: LeaderboardBoard[] = data?.leaderboard
    ? ([
        {
          key: "calls",
          title: "Calls",
          emoji: "📞",
          subtitle: null,
          entries: data.leaderboard.by_calls ?? [],
        },
        {
          key: "replies",
          title: "Replies",
          emoji: "💬",
          subtitle: null,
          entries: data.leaderboard.by_replies ?? [],
        },
        {
          key: "wins",
          title: "Wins",
          emoji: "🏆",
          subtitle: null,
          entries: data.leaderboard.by_wins ?? [],
        },
        {
          key: "activity",
          title: "Activity",
          emoji: "⚡",
          subtitle: null,
          entries: data.leaderboard.by_activity ?? [],
        },
      ] satisfies LeaderboardBoard[]).filter((b) => b.entries.length > 0)
    : (data?.leaderboards ?? []);

  // Breakdowns ship as flat fields; the nested form is the legacy
  // fallback.
  const breakdownsBySource =
    data?.by_source ?? data?.breakdowns?.by_source ?? [];
  const breakdownsBySector =
    data?.by_sector ?? data?.breakdowns?.by_sector ?? [];
  const breakdownsByFounderOrigin =
    data?.by_founder_origin ??
    data?.breakdowns?.by_founder_origin ??
    [];

  const recentWins = data?.recent_wins ?? [];
  const deals = data?.deal_attribution ?? data?.deals ?? [];

  return (
    <div className="space-y-6 pb-12">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
            Analytics
          </h1>
          <p className="text-sm text-slate-500">{project.name}</p>
        </div>
      </header>

      <AnalyticsHeader
        period={period}
        onPeriodChange={(p) => {
          setPeriod(p);
          if (p !== "custom") setCustomRange(null);
        }}
        customRange={customRange}
        onCustomRangeChange={setCustomRange}
        ownerId={ownerId}
        onOwnerChange={setOwnerId}
        members={members}
      />

      {status === "error" && (
        <div className="rounded-plane border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {errorMessage ?? "Couldn't load analytics."}{" "}
          <button
            type="button"
            onClick={() => void load()}
            className="ml-2 font-medium underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Section 1 — KPI overview */}
      <KpiCards kpis={kpis} loading={loading} />

      {/* Section 2 — Funnel + Conversion */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <section className="rounded-plane bg-white p-5 shadow-plane lg:col-span-7">
          <h2 className="mb-4 text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Pipeline funnel
          </h2>
          <FunnelChart
            stages={funnel}
            pipelineStatuses={pipelineStatuses}
            loading={loading}
          />
        </section>
        <section className="rounded-plane bg-white p-5 shadow-plane lg:col-span-5">
          <h2 className="mb-4 text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Conversion
          </h2>
          <ConversionMetrics metrics={conversion} loading={loading} />
        </section>
      </div>

      {/* Section 3 — Trends */}
      <section className="rounded-plane bg-white p-5 shadow-plane">
        <h2 className="mb-4 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Pipeline trends
        </h2>
        <TrendsChart
          series={trendsSeries}
          buckets={trendsBuckets}
          loading={loading}
        />
      </section>

      {/* Section 4 — Outreach */}
      <OutreachStats stats={outreach} loading={loading} />

      {/* Section 5 — Team performance */}
      <section>
        <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Team performance
        </h2>
        <TeamPerformance
          rows={team}
          loading={loading}
          selectedMemberId={ownerId === "all" ? null : ownerId}
          onSelectMember={(id) => setOwnerId(id ?? "all")}
        />
      </section>

      {/* Section 5b — Leaderboard podiums */}
      {(loading || leaderboards.length > 0) && (
        <section>
          <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-slate-500">
            Leaderboard
          </h2>
          <Leaderboard boards={leaderboards} loading={loading} />
        </section>
      )}

      {/* Section 6 — Breakdowns */}
      <section>
        <h2 className="mb-3 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Breakdowns
        </h2>
        <BreakdownCharts
          bySource={breakdownsBySource}
          bySector={breakdownsBySector}
          byFounderOrigin={breakdownsByFounderOrigin}
          loading={loading}
        />
      </section>

      {/* Section 7 — Recent wins */}
      <RecentWins
        wins={recentWins}
        projectSlug={slug ?? ""}
        loading={loading}
      />

      {/* Section 8 — Deal attribution journey. Wrapped in an
       *  ErrorBoundary so that even if a single malformed deal
       *  payload still crashes the section, the rest of the
       *  Analytics page stays visible above. */}
      <section>
        <h2 className="mb-3 flex items-center gap-2 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          <span aria-hidden>🏆</span>
          Deal Journey: How we won
        </h2>
        <ErrorBoundary
          fallback={
            <div className="rounded-plane bg-white p-4 text-sm text-slate-400 shadow-plane">
              Deal journey temporarily unavailable.
            </div>
          }
        >
          <DealAttribution
            deals={deals}
            projectSlug={slug ?? ""}
            loading={loading}
          />
        </ErrorBoundary>
      </section>
    </div>
  );
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "An unexpected error occurred.";
}
